import { ScopedTranslation } from "@app/shared/i18n/translation-scope";
import { ControlExportData } from "./models/ControlExportData";
import { nameof } from "@methods/jeffs-toolkit";
import { AdvancedEffectivenessExportData } from "./models/AdvancedEffectivenessExportData";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import {
    ParentChildButtonConfig,
    ParentChildFilterConfig,
    ParentChildSelectorConfig,
} from "@app/shared/models/ParentChildSelector/ParentChildSelectorConfig";

export class ControlCopyExportSharedMethods {
    public static createSelectorConfig(
        controls: ControlExportData[],
        buttonConfig: ParentChildButtonConfig[],
        translation: ScopedTranslation
    ): ParentChildSelectorConfig {
        // Both callers (copy / export effectiveness) await the "controls" scope before calling in.
        const t = (key: string) => translation.t(`copyExportShared.${key}`);

        const filters: ParentChildFilterConfig[] = [
            this.getDateFilter(t("filterStartDate"), (e) => e.startDate),
            this.getDateFilter(t("filterEndDate"), (e) => e.endDate),
            this.getMapChildFilter(controls, t("filterTestStatus"), (e) => e.statusDescription),
            this.getMapChildFilter(controls, t("filterOverdue"), (e) => e.isOverdue),
            this.getManyChildFilter(
                controls,
                t("filterSourceDocumentUploaders"),
                (e) => e.sourceDocumentUploaders
            ),
            this.getManyChildFilter(
                controls,
                t("filterEvidenceUploaders"),
                (e) => e.evidenceUploaders
            ),
            this.getManyChildFilter(controls, t("filterTesters"), (e) => e.testers),
            this.getManyChildFilter(controls, t("filterReviewers"), (e) => e.reviewers),
        ];

        return {
            items: controls,
            hideToolbar: false,

            parentIdentifierProperty: nameof<ControlExportData>((c) => c.guid),
            childIdentifierProperty: nameof<AdvancedEffectivenessExportData>((c) => c.guid),

            headerProp: nameof<ControlExportData>((c) => c.header),

            childPrettyName: t("childPrettyName"),
            childProperty: nameof<ControlExportData>((c) => c.effectivenessList),
            childNameProperty: nameof<AdvancedEffectivenessExportData>((eff) => eff.name),
            childToggleProperty: nameof<AdvancedEffectivenessExportData>((eff) => eff.isChecked),

            overviewProperties: [
                {
                    prettyName: t("overviewIdentifier"),
                    propertyName: nameof<ControlExportData>((c) => c.identifier),
                },
                {
                    prettyName: t("overviewName"),
                    propertyName: nameof<ControlExportData>((c) => c.name),
                },
                {
                    prettyName: t("overviewOrganization"),
                    propertyName: nameof<ControlExportData>((c) => c.organization),
                },
                {
                    prettyName: t("overviewRisks"),
                    propertyName: nameof<ControlExportData>((c) => c.risks),
                },
            ],

            filters: filters,

            buttons: buttonConfig,
            extraToggles: [],

            filterMethod: (allItems: ControlExportData[]) => {
                ControlCopyExportSharedMethods.filterMethod(allItems, filters);
            },
        };
    }

    public static getDateFilter(
        prettyName: string,
        callbackFn: (obj: AdvancedEffectivenessExportData) => string
    ) {
        return {
            prettyName: prettyName,
            propertyName: nameof<AdvancedEffectivenessExportData>(callbackFn),
            filterType: GenericListFieldType.Date,
            childFilter: true,
        };
    }

    public static getMapChildFilter(
        controls: ControlExportData[],
        prettyName: string,
        callbackFn: (obj: AdvancedEffectivenessExportData) => string
    ): ParentChildFilterConfig {
        return {
            prettyName: prettyName,
            propertyName: nameof<AdvancedEffectivenessExportData>(callbackFn),
            filterType: GenericListFieldType.MultiSelect,
            childFilter: true,

            data: controls
                .selectMany<ControlExportData, string>((c) => c.effectivenessList.map(callbackFn))
                .distinct()
                .sort(),
        };
    }

    public static getManyChildFilter(
        controls: ControlExportData[],
        prettyName: string,
        callbackFn: (obj: AdvancedEffectivenessExportData) => string[]
    ): ParentChildFilterConfig {
        return {
            prettyName: prettyName,
            propertyName: nameof<AdvancedEffectivenessExportData>(callbackFn),
            filterType: GenericListFieldType.MultiSelect,
            childFilter: true,

            data: controls
                .selectMany<ControlExportData, string>((c) =>
                    c.effectivenessList.selectMany<AdvancedEffectivenessExportData, string>(
                        callbackFn
                    )
                )
                .distinct()
                .sort(),
        };
    }

    public static filterMethod(
        allControls: ControlExportData[],
        filters: ParentChildFilterConfig[]
    ): void {
        let filtered = false;
        allControls.forEach((control) => {
            control.hide = false;
            control.effectivenessList.forEach((tp) => {
                tp.hide = false;
            });
        });

        const startDateFilter = filters.find(
            (f) =>
                f.propertyName === nameof<AdvancedEffectivenessExportData>((eff) => eff.startDate)
        );
        const endDateFilter = filters.find(
            (f) => f.propertyName === nameof<AdvancedEffectivenessExportData>((eff) => eff.endDate)
        );
        if (startDateFilter.value && endDateFilter.value) {
            filtered = true;
            allControls.forEach((control) => {
                control.effectivenessList.forEach((tp) => {
                    tp.hide =
                        tp.startDate > endDateFilter.value || tp.endDate < startDateFilter.value;
                    tp.selected = tp.selected && !tp.hide;
                });
            });
        }

        filters.forEach((filter) => {
            if (
                filter.value &&
                (!Array.isArray(filter.value) || (filter.value as []).length > 0) &&
                filter.filterType === GenericListFieldType.MultiSelect
            ) {
                filtered = true;
                const fValues = filter.value as [];

                allControls.forEach((control) => {
                    if (filter.childFilter) {
                        control.effectivenessList.forEach((tp) => {
                            tp.hide = tp.hide || !this.multiCheck(fValues, tp[filter.propertyName]);
                            tp.selected = tp.selected && !tp.hide;
                        });
                    } else {
                        control.hide =
                            control.hide || !this.multiCheck(fValues, control[filter.propertyName]);
                    }
                });
            }
        });

        if (filtered) {
            allControls.forEach((control) => {
                control.hide = !(control.effectivenessList as []).some(
                    (tp: any) => tp.hide !== true
                );
            });
        }
    }

    public static multiCheck(fValues: any[], value: any): boolean {
        if (Array.isArray(value)) {
            return fValues.some((filterValue: any) => (value as any[]).indexOf(filterValue) >= 0);
        } else {
            return fValues.some((filterValue: any) => filterValue === value);
        }
    }
}
